import React, { useCallback, useEffect, useRef, useState } from 'react';
import clipboardTypeIcon from '../../assets/icons/clipboard-type.svg';
import xMark from '../../assets/icons/x-mark.svg';
import {
    deleteFile,
    getFiles,
    uploadFile,
    type PatientFile,
    type PatientFileType
} from '../../services/cloud-files';

function useTimerCleanup() {
    const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

    const safeTimeout = useCallback((fn: () => void, ms: number) => {
        const id = setTimeout(() => {
            timers.current = timers.current.filter((t) => t !== id);
            fn();
        }, ms);
        timers.current.push(id);
    }, []);

    useEffect(() => {
        return () => {
            timers.current.forEach(clearTimeout);
        };
    }, []);

    return safeTimeout;
}

interface FileUploadCardProps {
    label: string;
    uploadLink?: string;
    icon?: string;
    isOptional?: boolean;
    hideIndicator?: boolean;
    /** Slim variant for tight sidebars: smaller icon, no tall min-height. */
    compact?: boolean;
    // Cloud files props
    patientId?: string;
    fileType?: PatientFileType;
    accept?: string;
    description?: string;
    metadata?: Record<string, unknown>;
    fileFilter?: (file: PatientFile) => boolean;
    timelineEventId?: string;
    onUploadComplete?: (file: PatientFile) => void;
    onDelete?: (patientFileId: string) => void;
    onUploadStateChange?: (isUploading: boolean, delta: 1 | -1) => void;
    onUploadedFilesChange?: (files: PatientFile[]) => void;
}

const FileUploadCard: React.FC<FileUploadCardProps> = ({
    label,
    uploadLink,
    icon,
    isOptional = false,
    hideIndicator = false,
    compact = false,
    patientId,
    fileType,
    accept,
    description,
    metadata,
    fileFilter,
    timelineEventId,
    onUploadComplete,
    onDelete,
    onUploadStateChange,
    onUploadedFilesChange
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploadedFiles, setUploadedFiles] = useState<PatientFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [currentFileName, setCurrentFileName] = useState<string>('');
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);
    const safeTimeout = useTimerCleanup();

    const fileFilterRef = useRef(fileFilter);
    fileFilterRef.current = fileFilter;

    const isDisabled = isUploading || isLoading || !patientId;

    const isFileAccepted = useCallback(
        (file: File): boolean => {
            if (!accept || accept === '*') return true;

            const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase());
            const fileName = file.name.toLowerCase();
            const fileMimeType = file.type.toLowerCase();

            return acceptedTypes.some((type) => {
                if (type.startsWith('.')) {
                    return fileName.endsWith(type);
                }
                if (type.endsWith('/*')) {
                    const category = type.slice(0, type.indexOf('/'));
                    return fileMimeType.startsWith(category + '/');
                }
                return fileMimeType === type;
            });
        },
        [accept]
    );

    const loadFiles = useCallback(async () => {
        if (!patientId || !fileType) return;

        setIsLoading(true);
        setError(null);
        try {
            const files = await getFiles(patientId, fileType, timelineEventId);
            setUploadedFiles(fileFilterRef.current ? files.filter(fileFilterRef.current) : files);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load files';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [patientId, fileType, timelineEventId]);

    useEffect(() => {
        if (patientId && fileType) {
            loadFiles();
        }
    }, [patientId, fileType, loadFiles]);

    useEffect(() => {
        onUploadedFilesChange?.(uploadedFiles);
    }, [uploadedFiles, onUploadedFilesChange]);

    const processFiles = useCallback(
        async (files: File[]) => {
            if (!patientId || !fileType) return;

            const rejected = files.filter((f) => !isFileAccepted(f));
            if (rejected.length > 0) {
                const allowed =
                    accept
                        ?.split(',')
                        .map((t) => t.trim())
                        .join(', ') ?? '';
                setError(
                    `Invalid file type${rejected.length > 1 ? 's' : ''}. Accepted: ${allowed}`
                );
                safeTimeout(() => setError(null), 5000);
            }

            const accepted = files.filter((f) => isFileAccepted(f));
            if (accepted.length === 0) return;

            setIsUploading(true);
            if (rejected.length === 0) setError(null);
            setUploadSuccess(false);
            onUploadStateChange?.(true, 1);

            const errors: string[] = [];
            for (const file of accepted) {
                setUploadProgress(0);
                setCurrentFileName(file.name);

                try {
                    const uploadedFile = await uploadFile(
                        patientId,
                        file,
                        fileType,
                        description,
                        metadata,
                        (progress) => setUploadProgress(progress),
                        timelineEventId
                    );
                    setUploadedFiles((prev) => [uploadedFile, ...prev]);
                    onUploadComplete?.(uploadedFile);
                } catch (err) {
                    const errorMessage =
                        err instanceof Error ? err.message : `Failed to upload ${file.name}`;
                    errors.push(errorMessage);
                }
            }

            if (errors.length > 0) {
                setError(errors.join('; '));
            } else {
                setUploadSuccess(true);
                safeTimeout(() => setUploadSuccess(false), 3000);
            }

            setIsUploading(false);
            setUploadProgress(0);
            setCurrentFileName('');
            onUploadStateChange?.(false, -1);
        },
        [
            patientId,
            fileType,
            description,
            metadata,
            accept,
            isFileAccepted,
            onUploadComplete,
            onUploadStateChange,
            safeTimeout,
            timelineEventId
        ]
    );

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) {
            if (e.currentTarget) {
                e.currentTarget.value = '';
            }
            return;
        }
        const fileList = Array.from(files);
        e.currentTarget.value = '';
        await processFiles(fileList);
    };

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (e.dataTransfer.types.includes('Files')) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current = 0;
            setIsDragging(false);

            if (isDisabled) return;

            const files = Array.from(e.dataTransfer.files);
            if (files.length === 0) return;

            await processFiles(files);
        },
        [isDisabled, processFiles]
    );

    const handleDelete = async (patientFileId: string) => {
        if (!patientId) return;

        try {
            await deleteFile(patientId, patientFileId);
            setUploadedFiles((prev) => prev.filter((f) => f.id !== patientFileId));
            onDelete?.(patientFileId);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete file';
            setError(errorMessage);
        }
    };

    // Legacy mode: use uploadLink if provided and no patientId
    if (uploadLink && !patientId) {
        const hasUploadLink = Boolean(uploadLink);
        return (
            <div className="border-2 border-dashed rounded-2xl p-4 text-center w-full min-h-48 flex flex-col justify-start items-stretch gap-3">
                <div className="flex flex-col items-center justify-start">
                    <img src={icon || clipboardTypeIcon} alt="" className="h-14 w-14 mb-2 mt-8" />
                    <p className="font-normal text-sm leading-6 text-center tracking-normal text-gray-800">
                        {label}
                        {!hideIndicator && !isOptional && (
                            <span className="text-red-500"> *</span>
                        )}{' '}
                        {!hideIndicator && isOptional && (
                            <span className="text-gray-500 text-xs italic">(Optional)</span>
                        )}
                    </p>
                    <button
                        disabled={!hasUploadLink}
                        onClick={() => window.open(uploadLink, '_blank')}
                        className="mt-2 text-sm font-bold leading-6 text-center tracking-wider text-[#3773A8] hover:underline disabled:text-gray-300 disabled:cursor-not-allowed"
                    >
                        Upload
                    </button>
                </div>
            </div>
        );
    }

    const borderClass = isDragging
        ? 'border-[#3773A8] bg-blue-50'
        : 'border-gray-300 hover:border-gray-400';

    const cursorClass = isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer';

    // Cloud files mode
    return (
        <div
            className={`border-2 border-dashed rounded-2xl text-center w-full flex flex-col justify-start items-stretch transition-colors duration-200 ${compact ? 'p-3 gap-2' : 'p-4 min-h-48 gap-3'} ${borderClass} ${cursorClass}`}
            onClick={() => !isDisabled && inputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <div className="flex flex-col items-center justify-start">
                <img
                    src={icon || clipboardTypeIcon}
                    alt=""
                    className={compact ? 'h-7 w-7 mb-1' : 'h-14 w-14 mb-2 mt-6'}
                />
                <p
                    className={`font-normal text-center tracking-normal text-gray-800 ${compact ? 'text-xs leading-5' : 'text-sm leading-6'}`}
                >
                    {label}
                    {!hideIndicator && !isOptional && <span className="text-red-500"> *</span>}{' '}
                    {!hideIndicator && isOptional && (
                        <span className="text-gray-500 text-xs italic">(Optional)</span>
                    )}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                    {isDragging
                        ? 'Drop files here'
                        : isUploading
                          ? 'Uploading...'
                          : 'Click or drag files here'}
                </p>
                <input
                    type="file"
                    ref={inputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept={accept}
                    multiple
                    disabled={isDisabled}
                />

                {/* Upload Progress */}
                {isUploading && uploadProgress > 0 && (
                    <div className="w-full mt-3 px-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-1.5">
                            <p
                                className="text-xs text-gray-600 truncate max-w-[70%]"
                                title={currentFileName}
                            >
                                {currentFileName}
                            </p>
                            <p className="text-xs text-gray-500 font-medium">
                                {Math.round(uploadProgress)}%
                            </p>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                                className="bg-[#3773A8] h-2 rounded-full transition-all duration-300"
                                style={{ width: `${Math.round(uploadProgress)}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Success Message */}
                {uploadSuccess && !isUploading && (
                    <div className="w-full mt-2 px-4">
                        <div className="flex items-center justify-center gap-1.5 text-green-600 bg-green-50 rounded py-1.5 px-3">
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2.5}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                            <p className="text-xs font-medium">Upload complete</p>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && !isUploading && (
                    <div className="w-full mt-2 px-4">
                        <div className="bg-red-50 rounded py-1.5 px-3">
                            <p className="text-xs text-red-600 text-center">{error}</p>
                        </div>
                    </div>
                )}
            </div>

            {uploadedFiles.length > 0 && (
                <ul
                    className="mt-2 text-left max-h-40 overflow-auto space-y-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    {uploadedFiles.map((file) => (
                        <li
                            key={file.id}
                            className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-green-600 text-xs">✓</span>
                                <span
                                    className="text-xs text-gray-700 truncate"
                                    title={file.file.originalFilename}
                                >
                                    {file.file.originalFilename}
                                </span>
                            </div>
                            <button
                                onClick={() => handleDelete(file.id)}
                                className="ml-2 p-1 rounded hover:bg-gray-200 transition-colors"
                                disabled={isLoading}
                            >
                                <img src={xMark} alt="Remove" className="w-3 h-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default FileUploadCard;
